import { Link } from '@tanstack/react-router';
import type { LinkProps } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import type { ElementType, ReactNode } from 'react';

// ─── List ─────────────────────────────────────────────────────────────────────

function List({ children }: { children: ReactNode }) {
  return (
    <div
      role="tablist"
      className="flex items-center gap-1 border-b border-border overflow-x-auto overflow-y-hidden"
    >
      {children}
    </div>
  );
}

// ─── Tab ──────────────────────────────────────────────────────────────────────

type TabProps = Omit<LinkProps, 'children'> & {
  icon: ElementType;
  children: ReactNode;
};

function Tab({ icon: Icon, children, ...linkProps }: TabProps) {
  return (
    <Link
      role="tab"
      {...linkProps}
      className={cn(
        'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
        'border-transparent text-muted-foreground hover:text-foreground'
      )}
      activeProps={{
        className: cn(
          'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
          'border-primary text-primary hover:text-primary'
        ),
        'aria-selected': true as never,
      }}
      inactiveProps={{ 'aria-selected': false as never }}
    >
      <Icon size={15} />
      {children}
    </Link>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

function PageTabsRoot({ children }: { children?: ReactNode }) {
  return <>{children}</>;
}

PageTabsRoot.List = List;
PageTabsRoot.Tab = Tab;

export { PageTabsRoot as PageTabs };
